import { getSupabaseClient } from "./supabase";
import { getResolvedWorkspace } from "./workspaceConfig";

/**
 * Calls one of apps/services' /integrations/nylas/* endpoints as the
 * signed-in tenant user - same session-token pattern telemetry already
 * uses. Returns null when Nylas isn't available here (demo preview, no
 * services URL) or the resource doesn't exist (404, e.g. "not connected") -
 * both are "there's nothing to show", never an error to the caller.
 */
export async function callNylasApi<T>(
  path: string,
  opts: {
    method?: "GET" | "POST";
    params?: Record<string, string>;
    body?: unknown;
  } = {},
): Promise<T | null> {
  const servicesUrl = (import.meta.env.VITE_CONTROL_PLANE_URL ?? "").replace(/\/+$/, "");
  const slug = getResolvedWorkspace()?.slug;
  if (!servicesUrl || !slug) return null;

  const { data } = await getSupabaseClient().auth.getSession();
  const token = data.session?.access_token;
  if (!token) return null;

  const query = new URLSearchParams({ tenant: slug, ...opts.params });
  const res = await fetch(`${servicesUrl}${path}?${query.toString()}`, {
    method: opts.method ?? "GET",
    headers: {
      authorization: `Bearer ${token}`,
      ...(opts.body ? { "content-type": "application/json" } : {}),
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const reason = await res
      .json()
      .then((b: { reason?: string }) => b.reason)
      .catch(() => undefined);
    throw new Error(reason ?? `Request failed (${res.status}).`);
  }
  return res.json();
}
